import { Dtos, NoteId, NoteTopicId, Relations } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminNoteTopicsQueryKey } from "../note.keys";
import { AdminNoteTopic } from "./types";

export const useCreateAdminNote = () => {
	const queryClient = useQueryClient();
	const { post } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();
	const { user } = useAuthenticatedUser();

	const mutation = useMutation({
		mutationFn: async ({
			content,
			topicId,
		}: {
			content: string;
			topicId: NoteTopicId;
			relation: Relations.Notes;
		}) =>
			post<Dtos.Admin.AdminNote.Create.Response>(`/v2/admin/admin-notes/note-topics/${topicId}`, {
				content,
			}),
		onMutate: async ({ content, relation, topicId }) => {
			await queryClient.cancelQueries({ queryKey: adminNoteTopicsQueryKey(relation) });

			const previousNotes = queryClient.getQueryData<AdminNoteTopic[]>(
				adminNoteTopicsQueryKey(relation),
			);

			const tempId = Math.random().toString(36).substring(7) as NoteId;

			queryClient.setQueryData(
				adminNoteTopicsQueryKey(relation),
				(old: AdminNoteTopic[] | undefined) => {
					const newNote = {
						id: tempId,
						content,
						topicId,
						fromUserFirstName: user.firstName,
						fromUserLastName: user.lastName,
						isPinned: false,
						isArchived: false,
						createdAt: new Date().toISOString(),
						updatedAt: new Date().toISOString(),
					};
					if (!old) throw new Error("No topics found");
					return old.map((topic) => {
						if (topic.id === topicId) {
							return {
								...topic,
								notes: [newNote, ...topic.notes],
							};
						}
						return topic;
					});
				},
			);
			return { previousNotes, tempId };
		},
		onSuccess: (data, { relation, topicId }, context) => {
			if (!context) throw new Error("No context found");
			queryClient.setQueryData(
				adminNoteTopicsQueryKey(relation),
				(old: AdminNoteTopic[] | undefined) => {
					if (!old) throw new Error("No topics found");
					return old.map((topic) => {
						if (topic.id === topicId) {
							return {
								...topic,
								notes: topic.notes.map((note) => {
									if (note.id === context.tempId) {
										return {
											...data,
										};
									}
									return note;
								}),
							};
						}
						return topic;
					});
				},
			);
		},
		onError: (error: Error, inputData, context) => {
			queryClient.setQueriesData(
				{ queryKey: adminNoteTopicsQueryKey(inputData.relation) },
				context?.previousNotes,
			);
			triggerErrorModal(error);
		},
	});

	return {
		createAdminNote: mutation.mutate,
		createAdminNoteIsLoading: mutation.isPending,
	};
};
